import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../services/api";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { UserPlus, Search, Check, Users, Plus, Loader2, Phone, Globe } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

interface Recipient {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company_name?: string;
}

interface AddRecipientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAddRecipient: (recipient: { name: string; email: string }) => void;
  existingRecipients?: { name: string; email: string }[];
}

const AddRecipientDialog: React.FC<AddRecipientDialogProps> = ({
  open,
  onOpenChange,
  onAddRecipient,
  existingRecipients = [],
}) => {
  const { t } = useTranslation(['documents', 'common']);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // New recipient form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [adding, setAdding] = useState(false);
  const storedUser = localStorage.getItem('user');
  const defaultLocale = (storedUser ? JSON.parse(storedUser).locale : 'en') || 'en';
  const [newLocale, setNewLocale] = useState(defaultLocale);

  const fetchRecipients = useCallback(async (search: string) => {
    try {
      setLoading(true);
      const params = `?search=${encodeURIComponent(search)}`;
      const response = await api.get<any>(`/recipients${params}`) as any;
      const data = response?.recipients || response?.data?.recipients || [];
      setSearchResults(data);
      setHasSearched(true);
    } catch (error) {
      console.error("Error fetching recipients:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm("");
      setDebouncedSearchTerm("");
      setSearchResults([]);
      setHasSearched(false);
      resetAddForm();
    }
  }, [open]);

  const resetAddForm = () => {
    setNewName("");
    setNewEmail("");
    setNewLocale(defaultLocale);
    setEmailError("");
    setShowAddForm(false);
  };

  // Debounce search term
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, open]);

  // Fetch when debounced term has 3+ chars
  useEffect(() => {
    if (!open) return;
    if (debouncedSearchTerm.trim().length >= 3) {
      fetchRecipients(debouncedSearchTerm.trim());
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [debouncedSearchTerm, open, fetchRecipients]);

  // Check if a user is already in the recipients list
  const isRecipientAlreadyAdded = (email: string): boolean => {
    return existingRecipients.some(
      (recipient) => recipient.email.toLowerCase() === email.toLowerCase()
    );
  };

  const handleSelectRecipient = (recipient: Recipient) => {
    onAddRecipient({ name: recipient.name, email: recipient.email });
    onOpenChange(false);
  };

  const isValidEmail = (email: string): boolean => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailBlur = () => {
    if (!newEmail.trim()) { setEmailError(""); return; }
    setEmailError(!isValidEmail(newEmail.trim()) ? t('documents:editor_email_invalid') : "");
  };

  const handleAddNewRecipient = async () => {
    if (!newName.trim() || !newEmail.trim()) return;
    if (!isValidEmail(newEmail.trim())) {
      setEmailError(t('documents:editor_email_invalid'));
      return;
    }

    try {
      setAdding(true);
      const payload: any = { name: newName.trim(), email: newEmail.trim(), locale: newLocale };
      const response = await api.post<any>("/recipients", payload) as any;
      const newRecipient = response?.recipient || response?.data?.recipient;
      if (newRecipient) {
        onAddRecipient({ name: newRecipient.name, email: newRecipient.email });
        onOpenChange(false);
        resetAddForm();
      }
    } catch (error: any) {
      console.error("Error adding recipient:", error);
    } finally {
      setAdding(false);
    }
  };

  const showSearchHint = searchTerm.trim().length > 0 && searchTerm.trim().length < 3;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="pb-4 pr-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 shrink-0 rounded-full bg-indigo-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <DialogTitle>{t('documents:editor_add_recipient_title')}</DialogTitle>
              <DialogDescription>
                {t('documents:editor_add_recipient_description')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search input */}
          <div className="flex items-center border border-indigo-200 rounded-lg bg-indigo-50/50 focus-within:ring-2 focus-within:ring-indigo-300 focus-within:border-indigo-300 transition-all">
            <div className="px-3 py-2">
              <Search className="h-4 w-4 text-indigo-400" />
            </div>
            <Input
              placeholder={t('documents:editor_search_placeholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              autoFocus
            />
          </div>

          {showSearchHint && (
            <p className="text-xs text-muted-foreground text-center">{t('documents:editor_search_hint')}</p>
          )}

          {/* Recipient list */}
          <div className="border border-gray-200 rounded-lg max-h-[300px] overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-indigo-600 mx-auto mb-2" />
                <span className="text-sm text-muted-foreground">{t('documents:editor_loading_recipients')}</span>
              </div>
            ) : !hasSearched && searchResults.length === 0 ? (
              <div className="p-8 text-center">
                <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <span className="text-sm text-muted-foreground">{t('documents:editor_search_hint')}</span>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="p-8 text-center">
                <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <span className="text-sm text-muted-foreground">{t('documents:editor_no_recipients')}</span>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {searchResults.map((recipient) => {
                  const isAdded = isRecipientAlreadyAdded(recipient.email);
                  return (
                    <div
                      key={recipient.id}
                      className={`p-3 transition-colors ${!isAdded ? 'hover:bg-indigo-50/60 cursor-pointer' : 'opacity-60'}`}
                      onClick={() => !isAdded && handleSelectRecipient(recipient)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-sm font-semibold text-gray-900">{recipient.name}</span>
                          <span className="text-xs text-gray-500 truncate">{recipient.email}</span>
                          {recipient.phone && (
                            <span className="text-xs text-muted-foreground flex items-center gap-1">
                              <Phone className="h-3 w-3" />{recipient.phone}
                            </span>
                          )}
                        </div>
                        {isAdded ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-600 border-emerald-200 bg-emerald-50 hover:bg-emerald-50 rounded-full text-xs px-3"
                            disabled
                          >
                            <Check className="h-3.5 w-3.5 mr-1" />
                            {t('documents:editor_added')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full text-xs px-4"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectRecipient(recipient);
                            }}
                          >
                            <UserPlus className="h-3.5 w-3.5 mr-1" />
                            {t('documents:editor_add')}
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add new recipient */}
          {!showAddForm ? (
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)} className="w-full">
              <UserPlus className="h-4 w-4 mr-1.5" />
              {t('documents:editor_add_new_recipient')}
            </Button>
          ) : (
            <div className="border rounded-lg p-3 space-y-3 bg-gray-50/50">
              <div className="space-y-2">
                <Label className="text-xs">{t('documents:editor_name_label')}</Label>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('documents:editor_name_placeholder')}
                  className="h-9 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">{t('documents:editor_email_label')}</Label>
                  <Input
                    type="email"
                    value={newEmail}
                    onChange={(e) => { setNewEmail(e.target.value); setEmailError(""); }}
                    onBlur={handleEmailBlur}
                    placeholder={t('documents:editor_email_placeholder')}
                    className={`h-9 text-sm ${emailError ? 'border-red-500' : ''}`}
                  />
                  {emailError && <p className="text-xs text-red-500">{emailError}</p>}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">{t('documents:editor_language')}</Label>
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground shrink-0" />
                    <Select value={newLocale} onValueChange={setNewLocale}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="en">{t('documents:editor_language_en')}</SelectItem>
                        <SelectItem value="fr">{t('documents:editor_language_fr')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddNewRecipient}
                  disabled={adding || !newName.trim() || !newEmail.trim() || !!emailError}
                  className="flex-1"
                >
                  {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                  {adding ? t('documents:editor_adding') : t('documents:editor_add_button')}
                </Button>
                <Button variant="ghost" size="sm" onClick={resetAddForm}>
                  {t('common:actions.cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddRecipientDialog;
